import type { PublicAvailabilitySelectionOption } from './schemas';

export const formatDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatDateTime = (value: string, timezone: string) =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  });

/**
 * Part-time and full-time providers are hired from a start date, so their
 * calendar offers only the earliest date they are available.
 */
export const isStartDateOnly = (listingKind: string) =>
  listingKind === 'PART_TIME' || listingKind === 'FULL_TIME';

/** The options on the earliest date among `options` (none when empty). */
export const earliestDateOptions = (options: readonly PublicAvailabilitySelectionOption[]) => {
  const earliest = options.map((option) => option.availabilityDate).sort()[0];
  return options.filter((option) => option.availabilityDate === earliest);
};

export const selectionOptionKey = (option: PublicAvailabilitySelectionOption) =>
  option.availabilityEntryIds.join(':');

/**
 * [MCV-1837] A part-time/full-time Begin starts from one option. When the earliest date has exactly
 * one, it is selected for the provider; when it has several (08:00-16:00 and 12:00-20:00) they
 * overlap and must not be held together, so nothing is selected until the provider picks one.
 */
export const autoSelectedStartKeys = (options: readonly PublicAvailabilitySelectionOption[]) => {
  const earliest = earliestDateOptions(options);
  return earliest.length === 1 ? earliest.map(selectionOptionKey) : [];
};

export const optionLabel = (option: PublicAvailabilitySelectionOption) => {
  const date = formatDate(option.availabilityDate);
  if (option.detailMode === 'DATE_ONLY') {
    return `${date} · Date-only availability`;
  }
  const time =
    option.startsAt && option.endsAt
      ? `${formatDateTime(option.startsAt, option.timezone)}–${formatDateTime(
          option.endsAt,
          option.timezone
        )}`
      : 'Exact shift';
  return [date, option.shiftLabel, option.shiftType, time, option.workLocation, option.stateCode]
    .filter(Boolean)
    .join(' · ');
};
